import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { OfferDemandDataModel } from '../../../types/indicators.interfaces';
import {
  BarGraph,
  Line,
  YAxis,
  YAxisLabel,
} from '../ReportValueOnMarketGraph/assetValueOnMarket.styles'; // Assuming you've created new  components
import {
  BarGroup,
  DemandBar,
  OfferBar,
  OfferDemandGraphContainer,
  RangeLabel,
  TagLabel,
  XAxis,
  XAxisLabel,
  XLabelsContainer,
} from './offerDemand.styles';

// Componente Tooltip personalizado
const CustomTooltip = ({ 
  active, 
  value, 
  label, 
  x, 
  y 
}: { 
  active: boolean; 
  value: number; 
  label: string; 
  x: number; 
  y: number; 
}) => {
  if (!active) return null;
  
  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y - 50,
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        zIndex: 1000,
        fontSize: '12px',
        fontWeight: 'bold',
        pointerEvents: 'none',
        transform: 'translateX(-50%)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}: {Math.round(value)}%
    </div>
  );
};

export const OfferDemandGraph = (props: OfferDemandDataModel) => {
  const intl = useIntl();
  const [tooltip, setTooltip] = useState<{
    active: boolean;
    value: number;
    label: string;
    x: number;
    y: number;
  }>({
    active: false,
    value: 0,
    label: '',
    x: 0,
    y: 0,
  });

  const categories =
    props.categories !== null
      ? props.categories.sort((a, b) => {
          if (a.category !== null && b.category !== null) {
            return a.category - b.category;
          }
          return 0;
        })
      : null; // Sort by category number

  // Extract offer and demand values
  const offers = categories?.map((item) => item.offer ?? 0) ?? [];
  const demands = categories?.map((item) => item.demand ?? 0) ?? [];

  // Find the maximum percentage to set Y-axis
  const maxPercentage = Math.max(...offers, ...demands, 0);

  const handleMouseEnter = (value: number, label: string, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const containerRect = event.currentTarget.closest('.offer-demand-graph')?.getBoundingClientRect();
    
    if (containerRect) {
      setTooltip({
        active: true,
        value,
        label,
        x: rect.left - containerRect.left + rect.width / 2,
        y: rect.top - containerRect.top,
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltip({
      active: false,
      value: 0,
      label: '',
      x: 0,
      y: 0,
    });
  };

  return (
    <OfferDemandGraphContainer className="offer-demand-graph" style={{ position: 'relative' }}>
      <BarGraph>
        <YAxis>
          {generatePercentageProgression(maxPercentage)
            .reverse()
            .map((label, index) => (
              <div
                style={{
                  width: '100%',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
                key={`item-${index}`}
              >
                <YAxisLabel key={index}>
                  <span>{label}%</span>
                </YAxisLabel>
                <Line key={index}></Line>
              </div>
            ))}
        </YAxis>
        <XAxis>
          {categories &&
            categories.map((item, index) => (
              <>
                {item.offer || item.demand ? (
                  <XAxisLabel key={index}>
                    <BarGroup>
                      {item.offer ? (
                        <OfferBar
                          height={`${
                            (item.offer * 100) /
                            generatePercentageProgression(maxPercentage).slice(
                              -1
                            )[0]
                          }%`}
                          onMouseEnter={(e: React.MouseEvent) => handleMouseEnter(item.offer!, intl.formatMessage({ id: 'offer' }), e)}
                          onMouseLeave={handleMouseLeave}
                          style={{ cursor: 'pointer' }}
                        />
                      ) : (
                        <></>
                      )}
                      {item.demand ? (
                        <DemandBar
                          height={`${
                            (item.demand * 100) /
                            generatePercentageProgression(maxPercentage).slice(
                              -1
                            )[0]
                          }%`}
                          onMouseEnter={(e: React.MouseEvent) => handleMouseEnter(item.demand!, intl.formatMessage({ id: 'demand' }), e)}
                          onMouseLeave={handleMouseLeave}
                          style={{ cursor: 'pointer' }}
                        />
                      ) : (
                        <></>
                      )}
                    </BarGroup>
                    <XLabelsContainer>
                      <RangeLabel>{item.range}</RangeLabel>
                      <TagLabel>{item.tag}</TagLabel>
                    </XLabelsContainer>
                  </XAxisLabel>
                ) : (
                  <XAxisLabel key={index}>
                    <BarGroup>
                      <OfferBar height={`0%`} />
                      <DemandBar height={`0%`} />
                    </BarGroup>
                    <XLabelsContainer>
                      <RangeLabel>{item.range}</RangeLabel>
                      <TagLabel>{item.tag}</TagLabel>
                    </XLabelsContainer>
                  </XAxisLabel>
                )}
              </>
            ))}
        </XAxis>
      </BarGraph>
      <CustomTooltip {...tooltip} />
    </OfferDemandGraphContainer>
  );
};

// Generate Y-axis labels as percentages up to the maximum percentage found
function generatePercentageProgression(maxHeightPercentage: number) {
  // Start with step of 5
  let step = 5;

  // Calculate how many values we would get with current step
  let numValues = Math.ceil(maxHeightPercentage / step) + 1;

  // If we have more than 7 values, increase the step size by multiples of 5
  while (numValues > 7) {
    step += 5;
    numValues = Math.ceil(maxHeightPercentage / step) + 1;
  }

  const max = Math.ceil(maxHeightPercentage / step) * step;
  const progression = [];
  for (let i = 0; i <= max; i += step) {
    progression.push(i);
  }
  return progression;
}
